import React from 'react';
import { classes, style } from 'typestyle';
import { TEAL_PRIMARY } from 'src/utils/Style/Theme';
import { CircularProgress } from '@material-ui/core';

export type SquareActionButtonType = 'primary' | 'secondary';
export type SquareActionButtonSize = 'small' | 'medium';

export interface SquareActionButtonProps {
  text: string;
  icon?: string;
  hidden?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onKeyPress?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  loading?: boolean;
  type?: SquareActionButtonType;
  size?: SquareActionButtonSize;
  disabled?: boolean;
  dataQa?: string;
  className?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;
}

const baseButton = style({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 0,
  border: `2px solid ${TEAL_PRIMARY}`,
  cursor: 'pointer',
  textTransform: 'uppercase',
  outline: 'none',
  padding: '0.4rem 1.2rem',
  fontFamily: '"Open Sans", sans-serif',
  lineHeight: 1.5,
  fontSize: '1rem',
  transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, opacity 0.15s ease',
  minHeight: 36,
  $nest: {
    '> i': {
      marginRight: 5,
    },
    '&:disabled': {
      cursor: 'not-allowed',
    },
  },
});

const primaryButton = style({
  $debugName: 'primaryButton',
  backgroundColor: TEAL_PRIMARY,
  borderColor: TEAL_PRIMARY,
  color: 'white',
  $nest: {
    '&:disabled': {
      backgroundColor: '#6cc3b9',
      color: 'white ',
      border: `2px solid #6cc3b9`,
    },
  },
});

const secondaryButton = style({
  $debugName: 'secondaryButton',
  backgroundColor: '#f1f1f1',
  borderColor: TEAL_PRIMARY,
  color: TEAL_PRIMARY,
  $nest: {
    '&:disabled': {
      color: '#c6c6c6',
      borderColor: '#dddddd',
      border: '2px solid',
    },
  },
});

const loadingButton = style({
  cursor: 'not-allowed',
  opacity: 1,
  pointerEvents: 'none',
});

const smallButton = style({
  padding: '0.25rem 0.9rem',
  fontSize: '0.875rem',
  minHeight: 32,
});

const iconContainer = style({
  marginRight: 5,
  display: 'inline-flex',
  alignItems: 'center',
});
export const SquareActionButton: React.FC<SquareActionButtonProps> = ({
  text,
  icon,
  onClick,
  loading = false,
  type = 'primary',
  size = 'medium',
  disabled = false,
  dataQa,
  className,
  onKeyPress,
  style: inlineStyle,
  hidden,
  ariaLabel,
}) => {
  const iconNode = loading ? (
    <CircularProgress size={size === 'small' ? 14 : 16} color="inherit" />
  ) : icon ? (
    <i className={icon} />
  ) : null;
  const variantClass = type === 'secondary' ? secondaryButton : primaryButton;
  const baseClasses = classes(baseButton, variantClass);
  const buttonClassName = classes(baseClasses, size === 'small' ? smallButton : '', loading ? loadingButton : '', className);

  return (
    <button
      type="button"
      onClick={onClick}
      onKeyPress={onKeyPress}
      className={buttonClassName}
      data-qa={dataQa}
      disabled={disabled || loading}
      aria-busy={loading}
      style={inlineStyle}
      hidden={hidden}
      aria-label={ariaLabel || text}
    >
      {iconNode ? <span className={iconContainer}>{iconNode}</span> : null}
      {text}
    </button>
  );
};

export default SquareActionButton;
